/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect } from "react";
import { Case, Switch } from "react-if";
import { Box } from "@nordcloud/gnui";
import { BreadcrumbsBox } from "~/components";
import { useQueryState } from "~/hooks";
import {
  EstateChartsQueryState,
  EstateGroupBy,
  useEstateChartParameters,
  EstateFiltersAndChartParams,
} from "./components";
import { EstateRecordsAccountsView } from "./EstateRecordsAccountsView";
import { EstateRecordsAccumulatedView } from "./EstateRecordsAccumulatedView";
import { EstateRecordsApplicationsView } from "./EstateRecordsApplicationsView";
import { EstateRecordsCategoryView } from "./EstateRecordsCategoryView";
import { EstateRecordsEnvironmentTypeView } from "./EstateRecordsEnvironmentTypeView";
import { EstateRecordsOrgUnitView } from "./EstateRecordsOrgUnitView";
import { EstateRecordsProviderView } from "./EstateRecordsProviderView";
import { EstateRecordsRegionView } from "./EstateRecordsRegionView";
import { EstateRecordsServiceView } from "./EstateRecordsServiceView";

const BREADCRUMBS = [
  {
    value: "estate-records",
    label: "Estate Records",
  },
];

const BREADCRUMBS_TAGS = [
  {
    color: "notification",
    text: "Beta",
  },
];

type Props = {
  envNid?: string;
};

export function EstateRecordsPage({ envNid }: Props) {
  const {
    state: { groupBy, periodStart, periodEnd },
    updateQueryState,
  } = useQueryState<EstateChartsQueryState>();

  const activeChart = groupBy ?? EstateGroupBy.NONE;

  useEffect(() => {
    updateQueryState({ page: undefined, query: undefined });
  }, [activeChart]);

  const {
    period,
    range,
    granularity,
    handlePeriodChange,
    handleGranularityChange,
    clearRange,
    isDataLoading,
    setIsDataLoading,
  } = useEstateChartParameters();

  function isChartVisible(chart: EstateGroupBy) {
    return chart === activeChart;
  }

  const preSelectedMonth = {
    from: periodStart ?? "",
    to: periodEnd ?? "",
  };

  return (
    <div>
      <BreadcrumbsBox
        title="Estate Records"
        labels={BREADCRUMBS}
        tags={BREADCRUMBS_TAGS}
      />
      <Box spacing="spacing04">
        <EstateFiltersAndChartParams
          period={period}
          range={range}
          granularity={granularity}
          disableDailyView={false}
          activeChart={activeChart}
          clearRange={clearRange}
          disabledFilters={isDataLoading}
          onPeriodChange={handlePeriodChange}
          onGranularityChange={handleGranularityChange}
        />
        <Switch>
          <Case condition={isChartVisible(EstateGroupBy.NONE)}>
            <EstateRecordsAccumulatedView
              envNid={envNid}
              period={period}
              range={preSelectedMonth}
              granularity={granularity}
              setIsDataLoading={setIsDataLoading}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.APP)}>
            <EstateRecordsApplicationsView
              granularity={granularity}
              period={period}
              range={preSelectedMonth}
              setIsDataLoading={setIsDataLoading}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.ENV)}>
            <EstateRecordsEnvironmentTypeView
              period={period}
              granularity={granularity}
              range={preSelectedMonth}
              setIsDataLoading={setIsDataLoading}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.ORG)}>
            <EstateRecordsOrgUnitView
              period={period}
              granularity={granularity}
              range={preSelectedMonth}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.PROVIDER)}>
            <EstateRecordsProviderView
              period={period}
              granularity={granularity}
              range={preSelectedMonth}
              setIsDataLoading={setIsDataLoading}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.REGION)}>
            <EstateRecordsRegionView
              period={period}
              granularity={granularity}
              range={preSelectedMonth}
              setIsDataLoading={setIsDataLoading}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.CATEGORY)}>
            <EstateRecordsCategoryView
              period={period}
              granularity={granularity}
              range={preSelectedMonth}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.ACCOUNT)}>
            <EstateRecordsAccountsView
              period={period}
              granularity={granularity}
              range={preSelectedMonth}
            />
          </Case>
          <Case condition={isChartVisible(EstateGroupBy.SERVICE)}>
            <EstateRecordsServiceView
              period={period}
              granularity={granularity}
              range={preSelectedMonth}
            />
          </Case>
        </Switch>
      </Box>
    </div>
  );
}
